import { z } from "zod";

export const slug = z
  .string()
  .regex(/^[a-z][a-z0-9_-]*$/)
  .max(100);
export const telemetryFilters = z.object({
  days: z.coerce.number().int().min(1).max(365).default(30),
  application: slug.optional(),
  operation: slug.optional(),
  workflow: slug.optional()
});
export const applicationsSchema = z.object({
  applications: z.array(
    z.object({
      schema_version: z.literal(1),
      application: slug,
      display_name: z.string().min(1),
      operations: z.array(
        z.object({ id: slug, workflows: z.array(slug), event_types: z.array(z.string()) })
      )
    })
  )
});
const count = z.number().int().nonnegative();
const duration = z.number().nonnegative();
const range = z.object({ start: z.string(), end: z.string(), days: z.number().int().positive() });
const run = z.object({
  runs: count,
  succeeded: count,
  failed: count,
  cancelled: count,
  success_rate: z.number().min(0).max(1),
  average_duration_seconds: duration
});
export const runSummarySchema = z.object({
  range,
  filters: telemetryFilters.omit({ days: true }),
  cohort_floor: z.number().int().positive(),
  overall: run.nullable(),
  by_application: z.array(run.extend({ application: slug })),
  by_operation: z.array(run.extend({ operation: slug })),
  by_workflow: z.array(run.extend({ application: slug, operation: slug, workflow: slug })),
  daily: z.array(run.extend({ date: z.string() }))
});
const citation = z.object({
  runs: count,
  total: count,
  verified: count,
  partial: count,
  unverified: count,
  skipped: count,
  type_a: count,
  type_a_rate: z.number().min(0).max(1),
  average_duration_seconds: duration
});
export const citationSummarySchema = z.object({
  range,
  filters: telemetryFilters.omit({ days: true, operation: true }),
  cohort_floor: z.number().int().positive(),
  overall: citation.nullable(),
  by_application: z.array(citation.extend({ application: slug })),
  daily: z.array(citation.extend({ date: z.string() }))
});
export type TelemetryApplication = z.infer<typeof applicationsSchema>["applications"][number];
export type RunSummary = z.infer<typeof runSummarySchema>;
export type CitationSummary = z.infer<typeof citationSummarySchema>;
export type TelemetryFilters = z.infer<typeof telemetryFilters>;

export function supportsCitations(
  applications: TelemetryApplication[],
  filters: TelemetryFilters
): boolean {
  return (
    (!filters.operation || filters.operation === "citation-audit") &&
    applications.some(
      (app) =>
        (!filters.application || app.application === filters.application) &&
        app.operations.some(
          (operation) =>
            operation.id === "citation-audit" &&
            operation.event_types.includes("citation_audit.completed") &&
            (!filters.workflow || operation.workflows.includes(filters.workflow))
        )
    )
  );
}

// Presentation only: identifiers and event attribution remain unchanged.
export function workflowLabel(id: string): string {
  return (
    (
      {
        "document-upload": "Document upload (CitateScreen)",
        pmcid: "PMCID (SciPubAudit)"
      } as Record<string, string>
    )[id] ?? id.replaceAll(/[-_]/g, " ")
  );
}

export type TrendPoint = { date: string; runs: number | null };

/**
 * One point per UTC calendar day from the range's start to its end, inclusive. Days the service
 * left out of `daily` (below the cohort floor, or no runs at all) are null: withheld, not zero.
 */
export function dailyTrend(
  range: { start: string; end: string },
  daily: { date: string; runs: number }[]
): TrendPoint[] {
  const day = (value: string) => Date.parse(`${value.slice(0, 10)}T00:00:00Z`);
  const start = day(range.start);
  const end = day(range.end);
  if (!Number.isFinite(start) || !Number.isFinite(end) || end < start) return [];
  const runs = new Map(daily.map((row) => [row.date.slice(0, 10), row.runs]));
  const points: TrendPoint[] = [];
  for (let at = start; at <= end && points.length <= 400; at += 86_400_000) {
    const date = new Date(at).toISOString().slice(0, 10);
    points.push({ date, runs: runs.get(date) ?? null });
  }
  return points;
}
